/**
 * One arrival, opened (#85).
 *
 * Reading, not working. Nothing about the job is editable here and there
 * is no action on it: what a Company does with a job happens in the
 * rental system that holds the job, and the one link out is the whole of
 * this screen's relationship with that system. Intake does not rebuild a
 * job list it cannot hold. Deleting is the list's, where several go at
 * once.
 *
 * The one thing that is editable is the call's summary and the person's
 * email (#89), committed when they are left — the settings save model,
 * not a form. What the Company wrote is theirs for good; nothing writes
 * over it.
 *
 * The kind is in the path because a call and a form submission are read
 * from different tables — a browser never hands over a uuid and asks
 * which one to try — and it is what decides whether there is a recording
 * to offer and whether what was said is a transcript or something
 * somebody typed.
 *
 * A Receptionist call has no audio and never will, because the
 * conversation is the transcript, so the player is absent on those rather
 * than present and broken. A number with recording switched off reads the
 * same way.
 *
 * This is the record (#115). The New Inquiry email carried the whole of
 * an Inquiry, the fact sheet, the Profile's answers and the Customer's
 * own words, while the pilot held that nobody signed in; it is the
 * notice now, and what it carried is read here, in the rows the server
 * builds from the same input the email is.
 *
 * This route is the arrival about no job: a call that became nothing, a
 * form that failed to draft. It draws it as a job of one on the same
 * page a job is read on (`components/arrivals/Record`), because a
 * Company reading a wrong number and a gala day in the same afternoon
 * should not be reading two products. An arrival that *is* about a job
 * is read on the job, with every other call about it in one thread, and
 * opening one here goes there — the link in an older New Inquiry email,
 * or a bookmark, lands on the whole conversation rather than one row of
 * it.
 */
import { useEffect } from "react";
import { Navigate, useLocation, useNavigate, useParams } from "react-router";
import { ArrivalRecord } from "../components/arrivals/Record";
import { useThread } from "../lib/thread";

export default function InquiryPage() {
  const { kind, id = "" } = useParams<{ kind: string; id: string }>();
  // The list the reader came from, carried on the link they followed, so
  // Back on this screen returns to the calls they were reading rather
  // than to an unfiltered list. Absent when the URL was opened directly.
  const state = useLocation().state as { from?: string } | null;
  const backTo = `/enquiries${state?.from ?? ""}`;
  const navigate = useNavigate();
  // An arrival about no job is a thread of one (#158): with it gone
  // there is nothing left to read, and the reader is put back on the
  // list.
  const thread = useThread({ kind, id }, () => navigate(backTo));

  useEffect(() => {
    document.title = "Enquiries | HireOS";
  }, []);

  // An arrival about a job is read on the job (#115). `replace`, so Back
  // from the job goes to wherever the reader came from rather than back
  // into this redirect.
  if (thread.movedTo) {
    return (
      <Navigate
        to={`/enquiries/${encodeURIComponent(thread.movedTo)}`}
        replace
        state={state}
      />
    );
  }

  return (
    <ArrivalRecord
      job={thread.job}
      loadError={thread.loadError}
      backTo={backTo}
      onEdited={thread.edited}
      onDelete={thread.removed}
      onSent={() => navigate(backTo)}
    />
  );
}
